import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const StadoFeatures = () => {
  const [selectedImg, setSelectedImg] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const features = [
    { 
      title: "Centralized Command Dashboard", 
      desc: "Eliminate screen-switching and get instant insights.", 
      tag: "Dashboard", 
      img: "/dashboard-1.png",
      video: "/dashboard-2.mp4"
    },
    { 
      title: "Intelligent Employee Management", 
      desc: "A digital filing cabinet that is searchable, secure, and smart.", 
      tag: "Employee", 
      img: "/employee.png",
      video: "/employee-1.mp4"
    },
    { 
      title: "Automated Attendance System", 
      desc: "Move beyond manual registers. Stado automates the clock-in process.", 
      tag: "Attendance", 
      img: "/attendance.png",
      video: "/attendance-1.mp4"
    },
    { 
      title: "GPS & Field Force Tracking", 
      desc: "Track on-field teams in real-time.", 
      tag: "Location", 
      img: "/location.png",
      video: "/location-1.mp4"
    },
    { 
      title: "Office Management System", 
      desc: "Manage branches and shifts.", 
      tag: "Office", 
      img: "/offices.png",
      video: "/office.mp4"
    },
    { 
      title: "Leave Management", 
      desc: "Digitize leave approvals.", 
      tag: "Leave", 
      img: "/leaves.png",
      video: "/leaves-1.mp4"
    },
    { 
      title: "Holiday Management", 
      desc: "Event and holiday calendar.", 
      tag: "Holidays", 
      img: "/holiday.png",
      video: "/holidays.mp4"
    },
    { 
      title: "Payroll Management", 
      desc: "Generate salaries, deductions and payslips automatically.", 
      tag: "Payroll", 
      img: "/Payroll-1.png",
      video: "/payroll.mp4"
    },
  ];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <div className="features-wrapper">
      <style>{`
        .features-wrapper {
          padding: 80px 5%;
          background: linear-gradient(160deg, #ffffff 0%, #fff5f8 100%);
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }

        .header-area { 
          text-align: center; 
          margin-bottom: 60px; 
        }

        .header-area h2 { 
          font-size: 42px; 
          font-weight: 800; 
          color: #1a1a1a; 
          margin-bottom: 15px; 
          text-transform: uppercase; 
          letter-spacing: -1px; 
          line-height: 1.1;
        }

        .highlight {
          background: linear-gradient(90deg, #ff3b69, #ff914d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .header-area p { 
          color: #666; 
          font-size: 18px; 
          max-width: 600px; 
          margin: 0 auto; 
        }

        /* Modern Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 30px;
          max-width: 1300px;
          margin: 0 auto;
        }

        /* Tablet */
        @media (max-width: 768px) {
          .header-area h2 {
            font-size: 32px;
          }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .header-area h2 {
            font-size: 26px;
            line-height: 1.2;
          }
        }

        .feature-card {
          background: #ffffff;
          border-radius: 28px;
          padding: 20px;
          box-shadow: 0 10px 30px rgba(255, 59, 105, 0.08);
          border: 1px solid rgba(255, 59, 105, 0.05);
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          z-index: 1;
          transition: all 0.3s ease;
        }

        .img-container {
          width: 100%;
          height: 220px;
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 20px;
          cursor: zoom-in;
        }

        .img-container img { 
          width: 100%; 
          height: 100%; 
          object-fit: cover; 
          transition: transform 0.5s ease; 
        }

        .feature-card:hover .img-container img { 
          transform: scale(1.1); 
        }

        .content h3 { 
          font-size: 20px; 
          color: #111; 
          margin-bottom: 10px; 
          font-weight: 700; 
          transition: color 0.3s; 
        }

        .content p { 
          font-size: 15px; 
          color: #555; 
          line-height: 1.5; 
          margin-bottom: 15px; 
          transition: color 0.3s; 
        }

        .tag { 
          font-size: 12px; 
          font-weight: 700; 
          color: #ff3b69; 
          text-transform: uppercase; 
          letter-spacing: 1px; 
        }

        .feature-btn {
          margin-top: auto;
          background: #f8f9fa;
          color: #111;
          border: none;
          padding: 12px;
          border-radius: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 15px;
        }

        /* Hover States */
        .feature-card:hover { 
          border-color: #ff3b69; 
          transform: translateY(-5px);
        }

        .feature-card:hover h3 { 
          color: #ff3b69; 
        }

        .feature-btn:hover { 
          background: #ff3b69; 
          color: white; 
          transform: translateY(-2px); 
        }

        /* Popups */
        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(10px);
        }

        .video-modal {
          position: relative;
          width: 90%;
          max-width: 1000px;
          border-radius: 24px;
          overflow: hidden;
          background: #000;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }

        .video-modal video {
          width: 100%;
          max-height: 80vh;
          display: block;
        }

        .close-btn {
          position: absolute;
          top: 15px;
          right: 20px;
          z-index: 10;
          color: white;
          cursor: pointer;
          font-size: 28px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(0,0,0,0.5);
          transition: all 0.3s ease;
        }

        .close-btn:hover {
          background: #ff3b69;
          transform: rotate(90deg);
        }
      `}</style>

      <div className="header-area" id="features">
        <motion.h2 
          initial={{ opacity: 0, y: -20 }} 
          animate={{ opacity: 1, y: 0 }}
        >
          Core <span className="highlight">Command Features</span>
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 0.2 }}
        >
          Powerful modules designed for productivity, automation, and accuracy.
        </motion.p>
      </div>

      <motion.div 
        className="features-grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {features.map((f, i) => (
          <motion.div 
            key={i} 
            className="feature-card" 
            variants={cardVariants}
          >
            <div 
              className="img-container" 
              onClick={() => setSelectedImg(f.img)}
            >
              <img src={f.img} alt={f.title} />
            </div>

            <div className="content">
              <span className="tag">{f.tag}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>

            <button
              className="feature-btn"
              onClick={() => setSelectedVideo(f.video)}
            >
              Watch Feature <span>→</span>
            </button>
          </motion.div>
        ))}
      </motion.div>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div 
            className="overlay" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImg(null)}
          >
            <motion.img 
              src={selectedImg} 
              initial={{ scale: 0.8 }} 
              animate={{ scale: 1 }}
              style={{ 
                maxWidth: '90%', 
                maxHeight: '80%', 
                borderRadius: '20px' 
              }} 
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            className="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedVideo(null)}
          >
            <motion.div
              className="video-modal"
              initial={{ scale: 0.8, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <div
                className="close-btn"
                onClick={() => setSelectedVideo(null)}
              >
                ✕
              </div>

              {/* Video */}
              <video
                src={selectedVideo}
                controls
                autoPlay
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StadoFeatures;